import React, { useState, useMemo } from 'react';
import { Input, Pagination, Button as AntButton, Dropdown } from 'antd';
import { SearchOutlined, FilterOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { Database, RefreshCw, Download, Loader2, Check } from 'lucide-react';
import styled from 'styled-components';
import { triggerConfetti } from '@/utils/confetti';
import PriceBadge from '@/components/ui/PriceBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import { AppModal } from '@/components/ui/AppModal';
import { AppSelect } from '@/components/ui/AppSelect';
import {
    TableWrapper,
    TableHeader,
    TableTitle,
    TableActions,
    TableSearchInput,
    TableActionButton,
    TableContent,
    StyledTable,
    TableHead,
    TableHeaderRow,
    TableHeaderCell,
    TableBody,
    EmptyStateCell,
    EmptyStateWrapper,
    EmptyStateIcon,
    EmptyStateText,
    TableRow,
    TableCell,
    ActionLinks,
    TablePagination,
    StyledPagination,
} from './style';

const Table = ({
    title,
    columns,
    dataSource,
    emptyText = 'No data',
    searchValue,
    onSearchChange,
    showSearch = true,
    showFilter = false,
    showMore = true,
    onRefresh,
    pagination = true,
    pageSize: initialPageSize = 10,
    isStatus = false,
    isDate = false,
    statusValue,
    onStatusChange,
    dateValue,
    onDateChange,
    statusOptions = [],
    dateOptions = [],
    $topMargin = true,
    onRowClick,
    getRowProps,
    serverPagination = false,
    currentPage: controlledCurrentPage,
    totalItems,
    onPageChange: controlledOnPageChange,
    showDownload = false,
    onExportExcel,
    onExportPdf,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [filterOpen, setFilterOpen] = useState(false);
    const [downloadState, setDownloadState] = useState({
        visible: false,
        format: null,
        progress: 0,
        total: 0,
        status: 'idle',
        estTime: '',
    });

    const handleExport = async (format) => {
        if (format === 'excel' && !onExportExcel) return;
        if (format === 'pdf' && !onExportPdf) return;

        const startTime = Date.now();
        setDownloadState({
            visible: true,
            format: format === 'excel' ? 'Excel' : 'PDF',
            progress: 0,
            total: 0,
            status: 'loading',
            estTime: 'Calculating...',
        });

        const updateProgress = (current, total) => {
            const elapsedMs = Date.now() - startTime;
            let estTimeStr = 'Estimating...';
            if (current > 0 && total > 0) {
                const msPerItem = elapsedMs / current;
                const remainingItems = total - current;
                const remainingMs = msPerItem * remainingItems;
                const remainingSec = Math.ceil(remainingMs / 1000);
                if (remainingSec <= 0) {
                    estTimeStr = 'Finishing...';
                } else if (remainingSec < 60) {
                    estTimeStr = `${remainingSec}s remaining`;
                } else {
                    const min = Math.floor(remainingSec / 60);
                    const sec = remainingSec % 60;
                    estTimeStr = `${min}m ${sec}s remaining`;
                }
            }
            setDownloadState((prev) => ({
                ...prev,
                progress: current,
                total: total,
                estTime: estTimeStr,
                status: current >= total ? 'done' : 'loading',
            }));
        };

        try {
            if (format === 'excel') {
                await onExportExcel(updateProgress);
            } else {
                await onExportPdf(updateProgress);
            }

            triggerConfetti();
            setDownloadState((prev) => ({
                ...prev,
                progress: prev.total,
                status: 'done',
                estTime: 'Success Celebration!',
            }));

            setTimeout(() => {
                setDownloadState((prev) => ({ ...prev, visible: false }));
            }, 2500);
        } catch (err) {
            console.error('Export failed:', err);
            setDownloadState((prev) => ({
                ...prev,
                status: 'error',
                estTime: 'Failed to export report',
            }));
            setTimeout(() => {
                setDownloadState((prev) => ({ ...prev, visible: false }));
            }, 3000);
        }
    };

    const hasData = Array.isArray(dataSource) && dataSource.length > 0;
    const showHeader = !!title || showSearch || showFilter || showMore;

    // Calculate paginated data
    const paginatedData = useMemo(() => {
        if (!pagination || serverPagination) return dataSource;
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return dataSource.slice(startIndex, endIndex);
    }, [dataSource, currentPage, pageSize, pagination, serverPagination]);

    const handlePageChange = (page, size) => {
        if (!serverPagination) {
            setCurrentPage(page);
        }
        if (size && size !== pageSize) {
            setPageSize(size);
        }
        if (serverPagination && controlledOnPageChange) {
            controlledOnPageChange(page, size || pageSize);
        }
    };

    const resolvedCurrentPage = serverPagination ? (controlledCurrentPage || 1) : currentPage;
    const resolvedTotal = serverPagination ? (totalItems ?? dataSource.length) : dataSource.length;
    const renderCell = (column, record, index) => {
        // If column has a custom render function, use it
        if (column.render) {
            return column.render(record[column.dataIndex], record, index);
        }

        // Special handling for plan column - use price badge
        if (column.dataIndex === 'plan') {
            return <PriceBadge>{record[column.dataIndex]}</PriceBadge>;
        }

        // Special handling for status column - use status badge
        if (column.dataIndex === 'status') {
            return <StatusBadge status={record[column.dataIndex]}>{record[column.dataIndex]}</StatusBadge>;
        }

        // Default: return the value
        const v = record[column.dataIndex];
        if (typeof v === 'number' && Number.isNaN(v)) return '—';
        return v;
    };

    return (
        <TableWrapper $topMargin={$topMargin}>
            {showHeader ? (
                <TableHeader>
                    {title && <TableTitle>{title}</TableTitle>}
                    <TableActions>
                        {showSearch && (
                            <TableSearchInput
                                placeholder="Search"
                                prefix={<SearchOutlined />}
                                value={searchValue}
                                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                            />
                        )}
                        {showFilter && (
                            <TableActionButton
                                disabled={!hasData}
                                onClick={() => {
                                    if (!hasData) return;
                                    setFilterOpen(true);
                                }}
                            >
                                <FilterOutlined />
                            </TableActionButton>
                        )}
                        {showDownload && (
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: 'excel',
                                            label: 'Export to Excel',
                                            icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
                                            onClick: () => handleExport('excel'),
                                        },
                                        {
                                            key: 'pdf',
                                            label: 'Export to PDF',
                                            icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
                                            onClick: () => handleExport('pdf'),
                                        },
                                    ],
                                }}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <TableActionButton
                                    type="button"
                                    aria-label="Download Reports"
                                    disabled={!hasData || downloadState.status === 'loading'}
                                >
                                    <Download size={20} />
                                </TableActionButton>
                            </Dropdown>
                        )}
                        {showMore && (
                            <TableActionButton
                                type="button"
                                aria-label="Refresh"
                                onClick={() => onRefresh && onRefresh()}
                            >
                                <RefreshCw size={20} />
                            </TableActionButton>
                        )}
                    </TableActions>
                </TableHeader>
            ) : null}
            <TableContent>
                <StyledTable>
                    <TableHead>
                        <TableHeaderRow>
                            {columns.map((column, index) => (
                                <TableHeaderCell key={column.key || column.dataIndex || index}>
                                    {column.title}
                                </TableHeaderCell>
                            ))}
                        </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length === 0 ? (
                            <TableRow $hoverable={false}>
                                <EmptyStateCell colSpan={columns.length}>
                                    <EmptyStateWrapper>
                                        <EmptyStateIcon>
                                            <Database size={20} />
                                        </EmptyStateIcon>
                                        <EmptyStateText>{emptyText}</EmptyStateText>
                                    </EmptyStateWrapper>
                                </EmptyStateCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((record, rowIndex) => {
                                const actualIndex = pagination ? (resolvedCurrentPage - 1) * pageSize + rowIndex : rowIndex;
                                const clickable = typeof onRowClick === 'function';
                                const rowProps = typeof getRowProps === 'function' ? (getRowProps(record, actualIndex) || {}) : {};
                                const rowStyle = rowProps.style || {};
                                return (
                                    <TableRow
                                        $hoverable={clickable}
                                        key={record.key || actualIndex}
                                        {...rowProps}
                                        onClick={(event) => {
                                            rowProps.onClick?.(event);
                                            if (!event.defaultPrevented && clickable) onRowClick(record);
                                        }}
                                        style={{ cursor: clickable ? 'pointer' : 'default', ...rowStyle }}
                                    >
                                        {columns.map((column, colIndex) => (
                                            <TableCell key={column.key || column.dataIndex || colIndex}>
                                                {renderCell(column, record, actualIndex)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </StyledTable>
                {pagination && dataSource.length > 0 && (
                    <TablePagination>
                        <StyledPagination
                            current={resolvedCurrentPage}
                            pageSize={pageSize}
                            total={resolvedTotal}
                            onChange={handlePageChange}
                            onShowSizeChange={handlePageChange}
                            showSizeChanger
                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                        />
                    </TablePagination>
                )}
            </TableContent>

            <AppModal
                title="Filters"
                open={filterOpen}
                onCancel={() => setFilterOpen(false)}
                onOk={() => setFilterOpen(false)}
                okText="Apply"
                size="sz"
                destroyOnClose
            >
                {isStatus ? (
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Status</div>
                        <AppSelect
                            value={statusValue}
                            onChange={(value) => onStatusChange && onStatusChange(value || undefined)}
                            options={statusOptions}
                            allowClear
                            placeholder="Select status"
                            size="large"
                            style={{ width: '100%' }}
                        />
                    </div>
                ) : null}

                {isDate ? (
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Date</div>
                        <AppSelect
                            value={dateValue}
                            onChange={onDateChange}
                            options={dateOptions}
                            allowClear
                            placeholder="Select date range"
                            size="large"
                            style={{ width: '100%' }}
                        />
                    </div>
                ) : null}
            </AppModal>

            <ProgressToast $visible={downloadState.visible} $status={downloadState.status}>
                <ProgressHeader>
                    <ProgressIconWrapper $status={downloadState.status}>
                        {downloadState.status === 'done' ? (
                            <Check size={16} strokeWidth={3} />
                        ) : (
                            <Loader2 size={16} className="spin-loader" />
                        )}
                    </ProgressIconWrapper>
                    <ProgressTitleContainer>
                        <ProgressTitleText>
                            {downloadState.status === 'done'
                                ? 'Export Complete'
                                : downloadState.status === 'error'
                                    ? 'Export Failed'
                                    : `Exporting ${downloadState.format}`}
                        </ProgressTitleText>
                        <ProgressSubtext>
                            {downloadState.status === 'done'
                                ? 'Your document is downloaded'
                                : downloadState.status === 'error'
                                    ? 'Please try again later'
                                    : `${downloadState.progress} of ${downloadState.total || 'some'} records`}
                        </ProgressSubtext>
                    </ProgressTitleContainer>
                    <ProgressPercentText>
                        {downloadState.status === 'done' ? '100%' : downloadState.total ? `${Math.round((downloadState.progress / downloadState.total) * 100)}%` : '0%'}
                    </ProgressPercentText>
                </ProgressHeader>

                <ProgressBarContainer>
                    <ProgressBarFill
                        $percentage={downloadState.status === 'done' ? 100 : downloadState.total ? (downloadState.progress / downloadState.total) * 100 : 0}
                        $status={downloadState.status}
                    />
                </ProgressBarContainer>

                <ProgressFooter>
                    <span style={{ opacity: 0.85 }}>{downloadState.status === 'done' ? 'Done!' : downloadState.estTime}</span>
                </ProgressFooter>
            </ProgressToast>
        </TableWrapper>
    );
};

const ProgressToast = styled.div`
    position: fixed;
    bottom: 32px;
    left: 32px;
    width: 340px;
    box-sizing: border-box;
    background: ${({ theme }) => {
        const isDark = theme.colors?.background?.default === '#141414';
        return isDark
            ? 'linear-gradient(135deg, rgba(28, 28, 30, 0.9) 0%, rgba(18, 18, 18, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(246, 247, 249, 0.95) 100%)';
    }};
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    border: 1px solid ${({ theme, $status }) => {
        if ($status === 'done') return 'rgba(39, 174, 96, 0.3)';
        if ($status === 'error') return 'rgba(235, 87, 87, 0.3)';
        const isDark = theme.colors?.background?.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';
    }};
    border-radius: 16px;
    padding: 16px 20px;
    box-shadow: ${({ theme }) => {
        const isDark = theme.colors?.background?.default === '#141414';
        return isDark
            ? '0 20px 40px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
            : '0 20px 40px rgba(84, 89, 104, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
    }};
    z-index: 10000;
    transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.4s cubic-bezier(0.165, 0.84, 0.44, 1), border-color 0.3s ease;
    transform: translateY(${({ $visible }) => ($visible ? '0' : '120px')}) scale(${({ $visible }) => ($visible ? '1' : '0.9')});
    opacity: ${({ $visible }) => ($visible ? '1' : '0')};
    pointer-events: ${({ $visible }) => ($visible ? 'all' : 'none')};
    font-family: var(--font-family-lufga), 'Inter', sans-serif;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const ProgressHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
`;

const ProgressIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: ${({ $status }) => {
        if ($status === 'done') return 'rgba(39, 174, 96, 0.12)';
        if ($status === 'error') return 'rgba(235, 87, 87, 0.12)';
        return 'rgba(240, 74, 74, 0.1)';
    }};
    color: ${({ $status }) => {
        if ($status === 'done') return '#27ae60';
        if ($status === 'error') return '#eb5757';
        return '#F04A4A';
    }};

    .spin-loader {
        animation: spin 1.2s linear infinite;
    }
`;

const ProgressTitleContainer = styled.div`
    flex: 1;
    min-width: 0;
`;

const ProgressTitleText = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => {
        const isDark = theme.colors?.background?.default === '#141414';
        return isDark ? '#ffffff' : '#1f1f1f';
    }};
    line-height: 1.2;
    margin-bottom: 2px;
`;

const ProgressSubtext = styled.div`
    font-size: 11px;
    color: ${({ theme }) => {
        const isDark = theme.colors?.background?.default === '#141414';
        return isDark ? '#8a8a8f' : '#8c8c8c';
    }};
`;

const ProgressPercentText = styled.div`
    font-size: 14px;
    font-weight: 800;
    color: ${({ theme }) => {
        const isDark = theme.colors?.background?.default === '#141414';
        return isDark ? '#ffffff' : '#F04A4A';
    }};
`;

const ProgressBarContainer = styled.div`
    width: 100%;
    height: 6px;
    background: ${({ theme }) => {
        const isDark = theme.colors?.background?.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)';
    }};
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 10px;
`;

const ProgressBarFill = styled.div`
    height: 100%;
    background: ${({ $status }) => {
        if ($status === 'done') return 'linear-gradient(90deg, #2ecc71, #27ae60)';
        if ($status === 'error') return 'linear-gradient(90deg, #ff7875, #ff4d4f)';
        return 'linear-gradient(90deg, #F04A4A, #ff7875)';
    }};
    border-radius: 10px;
    transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    width: ${({ $percentage }) => `${$percentage}%`};
`;

const ProgressFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${({ theme }) => {
        const isDark = theme.colors?.background?.default === '#141414';
        return isDark ? '#8a8a8f' : '#8c8c8c';
    }};
`;

export default Table;

